/**
 * Web Vitals Reporting
 *
 * Tracks Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint): Target < 2.5s
 * - CLS (Cumulative Layout Shift): Target < 0.1
 * - INP (Interaction to Next Paint): Target < 200ms
 * - FCP (First Contentful Paint): Target < 1.8s
 * - TTFB (Time to First Byte): Target < 800ms
 */

import { onCLS, onINP, onFCP, onLCP, onTTFB } from "web-vitals";

/**
 * Report web vitals to console (development) and analytics (production)
 * @param {Function} onPerfEntry - Optional callback for custom reporting
 */
const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    // Report all Core Web Vitals
    onCLS(onPerfEntry);
    onINP(onPerfEntry);
    onFCP(onPerfEntry);
    onLCP(onPerfEntry);
    onTTFB(onPerfEntry);
  }
};

/**
 * Send metrics to Google Analytics (if configured)
 */
export const sendToGoogleAnalytics = ({ name, delta, value, id }) => {
  // Check if Google Analytics is loaded
  if (window.gtag) {
    window.gtag("event", name, {
      event_category: "Web Vitals",
      value: Math.round(name === "CLS" ? delta * 1000 : delta),
      event_label: id,
      non_interaction: true,
    });
  }

  // Development logging (performance metrics reporting)
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log("Web Vital:", {
      name,
      value: Math.round(value),
      delta: Math.round(delta),
      id,
      rating: getRating(name, value),
    });
  }
};

/**
 * Get performance rating based on thresholds
 */
const getRating = (name, value) => {
  const thresholds = {
    LCP: { good: 2500, needsImprovement: 4000 },
    FCP: { good: 1800, needsImprovement: 3000 },
    CLS: { good: 0.1, needsImprovement: 0.25 },
    INP: { good: 200, needsImprovement: 500 },
    TTFB: { good: 800, needsImprovement: 1800 },
  };

  const threshold = thresholds[name];
  if (!threshold) return "unknown";

  if (value <= threshold.good) return "good";
  if (value <= threshold.needsImprovement) return "needs-improvement";
  return "poor";
};

export default reportWebVitals;
